import { USFWebSiteSchema } from "@sphtech/web2-seo-schema";
import ArticleCard from "@src/app/components/ArticleCard";
import CardsContainer from "@src/app/components/CardsContainer";
import EditorsPickCard from "@src/app/components/EditorsPickCard/EditorsPickCard";
import HeroGrid from "@src/app/components/HeroGrid";
import NewsletterSignUp from "@src/app/components/NewsletterSignUp";
import SectionContainer from "@src/app/components/SectionContainer/SectionContainer";
import { ThemeEnum } from "@src/app/components/ThemeProvider/ThemeProvider";
import {
  SEARCH_URL_PATTERN,
  SITE_NAME,
  SITE_URL,
} from "@src/app/constants/siteIdentity";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import type { BFFArticleCard } from "@src/app/types/bff";
import { createMetaTags } from "@src/app/utils/createMetaTags";
import { type MouseEvent, useEffect, useRef } from "react";

import { fetchHomePageData } from "./Home.server";
export const loader = fetchHomePageData;
export const meta = () => createMetaTags();

export default function HomePage() {
  const res = useRouteResponse("homePage");
  const sendDataToGTM = useGTMDispatch();
  const hasSentEditorsPickViewRef = useRef(false);
  const {
    heroArticles = [],
    editorsPickArticle,
    latestArticles = [],
    videoArticles = [],
    topArticles = [],
  } = res;

  const addTrackingRef = (article: BFFArticleCard, ref: string) => ({
    ...article,
    urlPath: article.urlPath ? `${article.urlPath}?ref=${ref}` : "",
  });

  const heroGridArticles = heroArticles
    .slice(0, 5)
    .map((a) => addTrackingRef(a, "top-hero"));

  const popularArticles = topArticles.map((a) =>
    addTrackingRef(a, "home-popular-this-month"),
  );

  const processedEditorsPick = editorsPickArticle
    ? addTrackingRef(editorsPickArticle, "home-editors-pick")
    : null;

  const processedLatestArticles = latestArticles.map((a) =>
    addTrackingRef(a, "home-latest-articles"),
  );

  const processedVideoArticles = videoArticles.map((a) =>
    addTrackingRef(a, "home-video-articles"),
  );

  useEffect(() => {
    if (!processedEditorsPick || hasSentEditorsPickViewRef.current) return;
    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: "editors pick",
      eventAction: "view",
      eventLabel: "editors pick",
    });
    hasSentEditorsPickViewRef.current = true;
  }, [processedEditorsPick, sendDataToGTM]);

  const handleHomeClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    if (!href) return;

    if (
      processedEditorsPick?.urlPath &&
      href.includes(processedEditorsPick.urlPath)
    ) {
      sendDataToGTM({
        event: "tcCustomEvent",
        eventCategory: "editors pick",
        eventAction: "click",
        eventLabel: processedEditorsPick.urlPath,
      });
    }
  };

  return (
    <div className="pb-10">
      {/* Universal SEO Framework (USF) - homepage only WebSite + Sitelinks Search Box */}
      <USFWebSiteSchema
        name={SITE_NAME}
        url={SITE_URL}
        searchUrl={SEARCH_URL_PATTERN}
      />

      {/* home hero story Q  */}
      {heroGridArticles.length > 0 ? (
        <SectionContainer showHeader={false}>
          <HeroGrid articles={heroGridArticles} />
        </SectionContainer>
      ) : null}

      {/* top articles  */}
      {popularArticles.length > 0 ? (
        <CardsContainer<BFFArticleCard>
          className="bg-background-secondary"
          articles={popularArticles}
          renderItem={(article: BFFArticleCard) => (
            <ArticleCard article={article} key={article.id} hideDate />
          )}
          title="Popular This Month"
          isSwiper
        />
      ) : null}

      <SectionContainer showHeader={false}>
        <NewsletterSignUp />
      </SectionContainer>

      {/* editors pick  */}
      {processedEditorsPick ? (
        <div onClickCapture={handleHomeClickCapture}>
          <EditorsPickCard
            {...processedEditorsPick}
            title={processedEditorsPick.title || ""}
            description={processedEditorsPick.description || ""}
            urlPath={processedEditorsPick.urlPath}
            imageUrl={processedEditorsPick.cover}
            category={processedEditorsPick.sectionName || ""}
            section={processedEditorsPick.section || ""}
          />
        </div>
      ) : null}

      {/* video article */}
      {processedVideoArticles.length > 0 ? (
        <CardsContainer<BFFArticleCard>
          articles={processedVideoArticles}
          renderItem={(article: BFFArticleCard) => (
            <ArticleCard
              article={article}
              theme={ThemeEnum.dark}
              key={article.id}
              hideDate
            />
          )}
          noIcon={false}
          iconSize={32}
          title="Video Articles"
          href="/tags/video"
          theme={ThemeEnum.dark}
          isSwiper
        />
      ) : null}

      {/* latest article  */}
      {processedLatestArticles.length > 0 ? (
        <CardsContainer<BFFArticleCard>
          articles={processedLatestArticles}
          renderItem={(article: BFFArticleCard) => (
            <ArticleCard
              article={article}
              variant="Gary"
              key={article.id}
              hideDate
            />
          )}
          title="Latest Articles"
        />
      ) : null}
    </div>
  );
}
